import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  StatCard,
  Tabs,
  SearchBox,
  NotificationTable,
  CreateNotificationModal,
  EditNotificationModal,
  DeleteConfirmModal,
  Pagination,
  PlusIcon,
  ChevronDownIcon,
} from "./components";
import { useModal } from "./hooks";
import {
  computeExpiryTime,
  formatNumber,
  scheduledSendTimeFrom,
  toEpochSeconds,
} from "./utils";
import { PLATFORM_OPTIONS, URGENCY_OPTIONS } from "./utils/constants";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";
import {
  listNotifications,
  createNotification,
  updateNotification,
  deleteNotification,
} from "./functions";

const ITEMS_PER_PAGE = 5;

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [doctorLookup, setDoctorLookup] = useState({});

  const createModal = useModal();
  const editModal = useModal();
  const deleteModal = useModal();

  useEffect(() => {
    const load = async () => {
      const data = await listNotifications();
      setNotifications(data);
    };
    load();
  }, []);

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const response = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: "GET" }
        );
        const data = await response.json();
        const lookup = {};
        (Array.isArray(data) ? data : []).forEach((doc) => {
          if (doc.user_id) {
            lookup[doc.user_id] = doc.user_name || doc.user_id;
          }
        });
        setDoctorLookup(lookup);
      } catch (err) {
        setDoctorLookup({});
      }
    };

    loadDoctors();
  }, []);

  const getAudienceLabel = (value) => {
    if (!value) return "—";
    if (value === "all_users") return "All Users";
    const ids = value.split(",").map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return "—";
    if (ids.length === 1) return doctorLookup[ids[0]] || ids[0];
    return ids.map((id) => doctorLookup[id] || id).join(", ");
  };

  const handleRowClick = (notification) => {
    setSelectedNotification(notification);
    editModal.open();
  };

  const handleEdit = (notification) => {
    setSelectedNotification(notification);
    editModal.open();
  };

  const handleDeleteClick = (notification) => {
    setSelectedNotification(notification);
    deleteModal.open();
  };

  const handleConfirmDelete = async () => {
    if (!selectedNotification) return;
    await deleteNotification(selectedNotification.id);
    setNotifications((prev) => prev.filter((n) => n.id !== selectedNotification.id));
    deleteModal.close();
    editModal.close();
    setSelectedNotification(null);
  };

  const handleUpdate = async (id, formData) => {
    const existing = notifications.find((n) => n.id === id);
    const scheduledSendTime = scheduledSendTimeFrom(formData);
    // Saving without republishing does not resend, so the expiry window keeps
    // measuring from when the notification goes out: its schedule if it has one,
    // otherwise the moment it was originally created. Drafts have not gone out
    // at all and stay open-ended until they are published.
    const isDraft = existing?.status === "draft";
    const isExpired = existing?.status === "expired";
    const sendTime = scheduledSendTime ?? toEpochSeconds(existing?.createdAt);
    const updatedFields = {
      title: formData.title,
      notificationMessage: formData.notificationMessage,
      targetAudience: formData.targetAudience,
      urgency: formData.urgency,
      platforms: formData.platforms,
      duration: formData.duration,
      scheduledSendTime,
      expiryTime: isDraft ? null : computeExpiryTime(formData.duration, sendTime),
      ...(!isDraft && !(isExpired && !scheduledSendTime) && {
        status: scheduledSendTime ? "scheduled" : "active",
      }),
    };
    const updated = await updateNotification(id, updatedFields);
    setNotifications((prev) => prev.map((n) => (n.id === id ? updated : n)));
  };

  const handleRepublish = async (id, formData) => {
    const scheduledSendTime = scheduledSendTimeFrom(formData);
    const updatedFields = {
      title: formData.title,
      notificationMessage: formData.notificationMessage,
      targetAudience: formData.targetAudience,
      urgency: formData.urgency,
      platforms: formData.platforms,
      duration: formData.duration,
      status: scheduledSendTime ? "scheduled" : "active",
      expiryTime: computeExpiryTime(formData.duration, scheduledSendTime),
      scheduledSendTime,
    };
    const updated = await updateNotification(id, updatedFields);
    setNotifications((prev) => prev.map((n) => (n.id === id ? updated : n)));
    editModal.close();
    setSelectedNotification(null);
  };

  const handleCreateSubmit = async (formData) => {
    const scheduledSendTime = scheduledSendTimeFrom(formData);
    const newNotification = {
      createdBy: localStorage.getItem("username") || undefined,
      title: formData.title,
      notificationMessage: formData.notificationMessage,
      targetAudience: formData.targetAudience,
      urgency: formData.urgency,
      platforms: formData.platforms,
      duration: formData.duration,
      status: scheduledSendTime ? "scheduled" : "active",
      recipientCount: 0,
      createdAt: Math.floor(Date.now() / 1000),
      expiryTime: computeExpiryTime(formData.duration, scheduledSendTime),
      scheduledSendTime,
    };
    const created = await createNotification(newNotification);
    setNotifications((prev) => [created, ...prev]);
  };

  const handleSaveDraft = async (formData) => {
    const newNotification = {
      createdBy: localStorage.getItem("username") || undefined,
      ...formData,
      status: "draft",
      recipientCount: 0,
      createdAt: Math.floor(Date.now() / 1000),
      expiryTime: null,
      scheduledSendTime: null,
    };
    const created = await createNotification(newNotification);
    setNotifications((prev) => [created, ...prev]);
  };

  const stats = {
    total: notifications.length,
    active: notifications.filter((n) => n.status === "active").length,
    scheduled: notifications.filter((n) => n.status === "scheduled").length,
    recipients: notifications.reduce((acc, n) => acc + n.recipientCount, 0),
  };

  const tabOptions = useMemo(() => {
    const countByStatus = notifications.reduce(
      (acc, notification) => {
        acc[notification.status] = (acc[notification.status] || 0) + 1;
        return acc;
      },
      { active: 0, scheduled: 0, expired: 0, draft: 0 }
    );

    return [
      { value: "all", label: "All", count: notifications.length },
      { value: "active", label: "Active", count: countByStatus.active },
      { value: "scheduled", label: "Scheduled", count: countByStatus.scheduled },
      { value: "expired", label: "Expired", count: countByStatus.expired },
      { value: "drafts", label: "Drafts", count: countByStatus.draft },
    ];
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const statusFilter = activeTab === "drafts" ? "draft" : activeTab;

    return notifications.filter((notification) => {
      if (statusFilter !== "all" && notification.status !== statusFilter) {
        return false;
      }

      if (urgencyFilter && notification.urgency !== urgencyFilter) {
        return false;
      }

      if (platformFilter) {
        const platforms = Array.isArray(notification.platforms) ? notification.platforms : [];
        if (!platforms.includes(platformFilter)) {
          return false;
        }
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchable = [
        notification.title,
        notification.notificationMessage,
        notification.targetAudience,
        notification.urgency,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [activeTab, notifications, searchQuery, urgencyFilter, platformFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE));
  const pagedNotifications = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredNotifications.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredNotifications]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchQuery, urgencyFilter, platformFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 bg-purple-50/30 min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-800">
          Notifications
        </h1>
        <Button variant="primary" onClick={createModal.open} icon={<PlusIcon />}>
          New Notification
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Notifications" value={stats.total} />
        <StatCard label="Active Now" value={stats.active} variant="success" />
        <StatCard label="Scheduled" value={stats.scheduled} />
        <StatCard label="Total Recipients" value={formatNumber(stats.recipients)} />
      </div>

      <Tabs tabs={tabOptions} activeTab={activeTab} onChange={setActiveTab} />

      <div className="bg-white border border-slate-200/50 rounded-xl overflow-hidden">
        <div className="flex justify-between items-center px-5 py-4 border-b border-slate-200/50">
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search notifications..."
          />
          <div className="flex gap-2">
            <div className="relative">
              <select
                value={urgencyFilter}
                onChange={(event) => setUrgencyFilter(event.target.value)}
                className="appearance-none inline-flex items-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200/50 
                  rounded-lg text-sm text-slate-500 cursor-pointer font-sans hover:border-slate-300 pr-8"
              >
                <option value="">All urgency</option>
                {URGENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative">
              <select
                value={platformFilter}
                onChange={(event) => setPlatformFilter(event.target.value)}
                className="appearance-none inline-flex items-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200/50 
                  rounded-lg text-sm text-slate-500 cursor-pointer font-sans hover:border-slate-300 pr-8"
              >
                <option value="">All platforms</option>
                {PLATFORM_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <NotificationTable
          notifications={pagedNotifications}
          onRowClick={handleRowClick}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          getAudienceLabel={getAudienceLabel}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredNotifications.length}
          itemsPerPage={ITEMS_PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      <CreateNotificationModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        onSubmit={handleCreateSubmit}
        onSaveDraft={handleSaveDraft}
      />

      <EditNotificationModal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        notification={selectedNotification}
        onUpdate={handleUpdate}
        onRepublish={handleRepublish}
        onDelete={handleDeleteClick}
        getAudienceLabel={getAudienceLabel}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleConfirmDelete}
        notificationTitle={selectedNotification?.title}
      />
    </div>
  );
}
